import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, CheckCircle, XCircle, ArrowLeft, RotateCcw, Trophy, Clock } from 'lucide-react';
import { quizzes } from '../data/mockData';
import { QuizQuestion } from '../types';
import Link from '../components/Link';

const QuizPage: React.FC = () => {
  const [currentQuiz] = useState(quizzes[0]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30); // 30 seconds per question
  const [isTimerActive, setIsTimerActive] = useState(true);

  const currentQuestion = currentQuiz.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === currentQuiz.questions.length - 1;

  // Timer effect
  useEffect(() => {
    if (!isTimerActive || showResult || quizCompleted) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleNextQuestion();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentQuestionIndex, isTimerActive, showResult, quizCompleted]);

  // Reset timer when question changes
  useEffect(() => {
    setTimeLeft(30);
    setSelectedAnswer('');
    setShowResult(false);
  }, [currentQuestionIndex]);

  const handleAnswerSelect = (answer: string) => {
    if (showResult) return;
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = () => {
    if (!selectedAnswer) return;
    
    setIsTimerActive(false);
    setShowResult(true);
    
    const newAnswers = [...userAnswers, selectedAnswer];
    setUserAnswers(newAnswers);
    
    if (selectedAnswer.toLowerCase() === currentQuestion.correctAnswer.toLowerCase()) {
      setScore(score + 1);
    }
  };

  const handleNextQuestion = () => {
    if (isLastQuestion) {
      setQuizCompleted(true);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setIsTimerActive(true);
    }
  };

  const restartQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer('');
    setUserAnswers([]);
    setShowResult(false);
    setQuizCompleted(false);
    setScore(0);
    setTimeLeft(30);
    setIsTimerActive(true);
  };

  const getScoreColor = () => {
    const percentage = (score / currentQuiz.questions.length) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreMessage = () => {
    const percentage = (score / currentQuiz.questions.length) * 100;
    if (percentage >= 80) return 'Excellent! Your biblical knowledge is strong.';
    if (percentage >= 60) return 'Good work! Keep studying God\'s word.';
    return 'Keep growing! Every step in faith matters.';
  };

  if (quizCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 text-center max-w-lg w-full"
        >
          <Trophy className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-blue-900 mb-2">Quiz Completed!</h2>
          <p className="text-gray-600 mb-6">Great job on completing this week's growth quiz</p>
          
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <div className={`text-4xl font-bold mb-2 ${getScoreColor()}`}>
              {score}/{currentQuiz.questions.length}
            </div>
            <div className="text-lg text-gray-700 mb-2">
              {Math.round((score / currentQuiz.questions.length) * 100)}% Correct
            </div>
            <p className="text-gray-600">{getScoreMessage()}</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={restartQuiz}
              className="flex items-center justify-center px-6 py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              Try Again
            </button>
            <Link
              to="/"
              className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back Home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-blue-600 hover:text-blue-800 transition-colors">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div className="flex items-center space-x-2">
                <Brain className="h-8 w-8 text-amber-500" />
                <h1 className="text-2xl font-bold text-blue-900">{currentQuiz.title}</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Question {currentQuestionIndex + 1} of {currentQuiz.questions.length}
              </div>
              <div className="flex items-center text-amber-600">
                <Clock className="h-4 w-4 mr-1" />
                <span className="font-mono font-bold">{timeLeft}s</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{Math.round(((currentQuestionIndex + 1) / currentQuiz.questions.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-amber-500 to-amber-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ 
                width: `${((currentQuestionIndex + 1) / currentQuiz.questions.length) * 100}%` 
              }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Timer Bar */}
        <div className="mb-8">
          <div className="w-full bg-gray-200 rounded-full h-1">
            <motion.div
              className="bg-gradient-to-r from-green-500 to-red-500 h-1 rounded-full"
              animate={{ width: `${(timeLeft / 30) * 100}%` }}
              transition={{ duration: 1 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-xl p-8 mb-8"
          >
            <h2 className="text-2xl font-bold text-blue-900 mb-6 leading-relaxed">
              {currentQuestion.question}
            </h2>

            {currentQuestion.type === 'multiple-choice' ? (
              <div className="space-y-4">
                {currentQuestion.options?.map((option, index) => (
                  <motion.button
                    key={option}
                    onClick={() => handleAnswerSelect(option)}
                    disabled={showResult}
                    whileHover={!showResult ? { scale: 1.02 } : {}}
                    whileTap={!showResult ? { scale: 0.98 } : {}}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selectedAnswer === option
                        ? showResult
                          ? option === currentQuestion.correctAnswer
                            ? 'border-green-500 bg-green-50 text-green-800'
                            : 'border-red-500 bg-red-50 text-red-800'
                          : 'border-amber-500 bg-amber-50 text-amber-800'
                        : showResult && option === currentQuestion.correctAnswer
                        ? 'border-green-500 bg-green-50 text-green-800'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                    } ${showResult ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {String.fromCharCode(97 + index)}. {option}
                      </span>
                      {showResult && (
                        <div>
                          {option === currentQuestion.correctAnswer && (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          )}
                          {selectedAnswer === option && option !== currentQuestion.correctAnswer && (
                            <XCircle className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={selectedAnswer}
                  onChange={(e) => handleAnswerSelect(e.target.value)}
                  disabled={showResult}
                  className={`w-full p-4 text-lg border-2 rounded-xl focus:outline-none transition-colors ${
                    showResult
                      ? selectedAnswer.toLowerCase() === currentQuestion.correctAnswer.toLowerCase()
                        ? 'border-green-500 bg-green-50 text-green-800'
                        : 'border-red-500 bg-red-50 text-red-800'
                      : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Type your answer here..."
                />
                {showResult && selectedAnswer.toLowerCase() !== currentQuestion.correctAnswer.toLowerCase() && (
                  <p className="mt-2 text-green-600 font-medium">
                    Correct answer: {currentQuestion.correctAnswer}
                  </p>
                )}
              </div>
            )}

            {showResult && currentQuestion.explanation && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200"
              >
                <h4 className="font-semibold text-blue-900 mb-2">Explanation:</h4>
                <p className="text-blue-800">{currentQuestion.explanation}</p>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Action Buttons */}
        <div className="flex justify-center">
          {!showResult ? (
            <motion.button
              onClick={handleSubmitAnswer}
              disabled={!selectedAnswer}
              whileHover={selectedAnswer ? { scale: 1.05 } : {}}
              whileTap={selectedAnswer ? { scale: 0.95 } : {}}
              className="px-8 py-4 bg-amber-500 text-white rounded-xl font-bold text-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-amber-600 transition-all duration-300 shadow-lg"
            >
              Submit Answer
            </motion.button>
          ) : (
            <motion.button
              onClick={handleNextQuestion}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition-all duration-300 shadow-lg"
            >
              {isLastQuestion ? 'Finish Quiz' : 'Next Question'}
            </motion.button>
          )}
        </div>
      </main>
    </div>
  );
};

export default QuizPage;